import { getCurrentInstance, onBeforeUnmount, ref } from 'vue'
import {
  ActionCableChannelState,
  useActionCableStateStore,
} from '@/shared/stores/actionCableState'
import { isMentorTour } from '@/modules/mentor-tour/demoMode'

type Cable = {
  subscribe: (params: { channel: string; room?: string }) => void
  unsubscribe: (channelName: string, room?: string) => void
  perform: (params: { channel: string; action: string; data: unknown }) => void
  _addChannel: (
    channelName: string,
    callbacks: {
      connected?: () => void
      rejected?: (error: unknown) => void
      received?: (data: unknown) => void
      disconnected?: () => void
    },
    meta: { _uid: number; _value: unknown },
  ) => void
}

export const useActionCable = ({
  channelName,
  onConnected,
  onReceived,
  onRejected,
  onDisconnected,
}: {
  channelName: string
  onConnected?: () => void
  onReceived?: (data: unknown) => void
  onRejected?: (error: unknown) => void
  onDisconnected?: () => void
}) => {
  const instance = getCurrentInstance()

  if (!instance || !instance.proxy) {
    throw new Error('useActionCable must be called within setup')
  }

  const cable = (instance.proxy as unknown as { $cable: Cable }).$cable
  const subscribedRoom = ref<string | null>(null)
  const hasRegisteredSubscriber = ref(false)
  const actionCableStateStore = useActionCableStateStore()
  const channelState = actionCableStateStore.getChannelState(channelName)
  const isChannelSubscribed =
    actionCableStateStore.isChannelSubscribed(channelName)

  const subscribeChannel = (options?: { room?: string | null }) => {
    // A subscription opens the socket. The mentor tour has no socket.
    if (isMentorTour()) return

    if (!hasRegisteredSubscriber.value) {
      actionCableStateStore.registerSubscriber(channelName)
      hasRegisteredSubscriber.value = true
    }

    const nextState =
      channelState.value === ActionCableChannelState.DISCONNECTED ||
      channelState.value === ActionCableChannelState.REJECTED
        ? ActionCableChannelState.CONNECTING
        : channelState.value
    actionCableStateStore.setChannelState(channelName, nextState)

    const room = options?.room ?? ''
    cable.subscribe({ channel: channelName, ...(room.length > 0 && { room }) })
    if (room.length > 0) {
      subscribedRoom.value = room
    }
    cable._addChannel(
      channelName,
      {
        connected: () => {
          actionCableStateStore.setChannelState(
            channelName,
            ActionCableChannelState.SUBSCRIBED,
          )
          onConnected?.()
        },
        rejected: (error) => {
          actionCableStateStore.setChannelState(
            channelName,
            ActionCableChannelState.REJECTED,
          )
          onRejected?.(error)
        },
        received: onReceived,
        disconnected: () => {
          actionCableStateStore.setChannelState(
            channelName,
            ActionCableChannelState.DISCONNECTED,
          )
          onDisconnected?.()
        },
      },
      {
        _uid: instance.uid,
        _value: instance.proxy,
      },
    )
  }

  const unsubscribeChannel = (options?: { room?: string | null }) => {
    const room = options?.room
    if (room && room.length > 0) {
      cable.unsubscribe(channelName, room)
    } else {
      cable.unsubscribe(channelName)
    }

    if (hasRegisteredSubscriber.value) {
      actionCableStateStore.unregisterSubscriber(channelName)
      hasRegisteredSubscriber.value = false
    }
  }

  const performAction = ({
    action,
    data,
  }: {
    action: string
    data: unknown
  }) => {
    cable.perform({
      channel: channelName,
      action,
      data,
    })
  }

  onBeforeUnmount(() => {
    unsubscribeChannel({ room: subscribedRoom.value })
  })

  return {
    subscribeChannel,
    unsubscribeChannel,
    performAction,
    channelState,
    isChannelSubscribed,
  }
}
